import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useKBar } from 'kbar';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { CustomizeSidebarModal } from '@/components/modals/CustomizeSidebarModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { CoachMark } from '@/components/ui/coach-mark';
import { Rail } from '@/design-system-v2/rail';
import { CoachMarkTip } from '@/features/coach-marks';
import {
  CONTEXT_COLLECTION_OFF_COACH_MARK,
  ContextCollectionMenu,
} from '@/features/context-collection';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { ActiveMeetingPill } from '@/features/meetings/meetings-active.components';
import {
  useMeetingNotesIntentPrefetch,
  useOpenMeetingsLanding,
} from '@/features/meetings/meetings.hooks';
import { webOnboardingThreadsStore } from '@/features/onboarding-web';
import { QuickCreateRail } from '@/features/quick-create/quick-create-rail';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { dismissWelcomePage, useWelcomePageEnabled } from '@/features/welcome';
import { pluginsStore } from '@/plugins/plugins.store';
import { useDrawerNavigation } from '@/providers/DrawerNavigationProvider';
import { type MainSidebarNavItem, uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';

import {
  NavRailExtras,
  NavRailMore,
  NavRailProfile,
  NavRailUpdate,
  type RailItem,
} from './nav-rail-menus';
import { useNavRailPanel } from './nav-rail.hooks';
import { navRailPanelFor } from './nav-rail.utils';

export const NavRail = observer(() => {
  const hasFeature = useFeature();
  const { query } = useKBar();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const panel = useNavRailPanel();
  const prefetchMeetingNotes = useMeetingNotesIntentPrefetch();
  const openMeetingsLanding = useOpenMeetingsLanding();
  const welcomeEnabled = useWelcomePageEnabled();
  const { openTab } = useTabs();
  const drawer = useDrawerNavigation();

  const openRoute = (
    route: Routes | `/p/${string}`,
    event?: React.MouseEvent,
    openDestination?: () => void
  ) => {
    const search = route === Routes.Chats ? { new: true } : {};
    const inNewTab = event !== undefined && isNewTabClick(event);
    if (inNewTab) {
      openTab({ path: route, search });
      return;
    }
    if (
      uiStore.isNarrowViewport &&
      pathname === route &&
      panel !== null &&
      navRailPanelFor(route) === panel
    ) {
      drawer.setIsOpen(!drawer.isOpen);
      return;
    }
    if (openDestination) {
      openDestination();
      return;
    }
    void navigate({ to: route as string, search });
  };

  const routeItem = (
    route: Routes,
    label: string,
    icon: React.ReactNode,
    onIntent?: () => void
  ) => (
    <Rail.Button
      label={label}
      active={pathname.startsWith(route)}
      onIntent={onIntent}
      onClick={(event) => openRoute(route, event)}
    >
      {icon}
    </Rail.Button>
  );

  const allItems: RailItem[] = [
    {
      id: 'meetings',
      label: 'Meeting Notes',
      caption: 'Meetings',
      icon: <Icons.meetings strokeWidth={1.75} />,
      route: Routes.Meetings,
      onIntent: prefetchMeetingNotes,
      onClick: (event) =>
        openRoute(Routes.Meetings, event, () => void openMeetingsLanding()),
    },
    {
      id: 'feed',
      label: 'Feed',
      icon: <Icons.galleryVerticalEnd strokeWidth={1.75} />,
      route: Routes.Feed,
      enabled: hasFeature(FEATURES.ACTION_CARDS_FEED),
    },
    {
      id: 'agents',
      label: 'Agents',
      icon: <Icons.bot strokeWidth={1.75} />,
      route: Routes.AgentsNewTask,
      active: panel === 'agents',
      enabled: hasFeature(FEATURES.AGENTS_UX),
    },
    {
      id: 'journal',
      label: 'Daily Journal',
      caption: 'Journal',
      icon: <Icons.journal strokeWidth={1.75} />,
      route: Routes.Journal,
      enabled: hasFeature(FEATURES.JOURNALS),
    },
    {
      id: 'wiki',
      label: 'Wiki',
      icon: <Icons.book strokeWidth={1.75} />,
      route: Routes.PeopleWiki,
      enabled: hasFeature(FEATURES.PEOPLE_WIKI),
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <Icons.playground strokeWidth={1.75} />,
      route: Routes.Integrations,
    },
    {
      id: 'library',
      label: 'Library',
      icon: <Icons.gridPlus strokeWidth={1.75} />,
      route: Routes.Sprites,
    },
  ];
  const items = allItems.filter((item) => item.enabled !== false);

  const hasExtras = userStore.isInternalUser;
  const isShown = (id: MainSidebarNavItem) => {
    if (id === 'agents') return uiStore.mainSidebarAgentsShown;
    if (id === 'extras') return uiStore.mainSidebarExtrasShown;
    return uiStore.mainSidebarNavItems.includes(id);
  };
  const shownItems = items.filter((item) => isShown(item.id));
  const hiddenItems = items.filter((item) => !isShown(item.id));
  const showExtras = hasExtras && isShown('extras');

  const selectItem = (item: RailItem, event?: React.MouseEvent) =>
    item.onClick
      ? item.onClick(event)
      : item.route && openRoute(item.route, event);

  const isItemActive = (item: RailItem) =>
    item.active ?? (!!item.route && pathname.startsWith(item.route));

  const railItem = (item: RailItem) => (
    <Rail.Button
      key={item.id}
      id={`${item.id}-sidebar-item`}
      label={item.label}
      caption={item.caption}
      active={isItemActive(item)}
      onIntent={item.onIntent}
      onClick={(event) => selectItem(item, event)}
    >
      {item.icon}
    </Rail.Button>
  );

  const hideWelcome = () => {
    dismissWelcomePage();
    if (pathname.startsWith(Routes.Welcome)) openRoute(Routes.Chats);
  };

  const openCustomize = () =>
    ModalsManager.show(
      CustomizeSidebarModal,
      {
        items: [
          ...items,
          ...(hasExtras
            ? [
                {
                  id: 'extras' as const,
                  label: 'Extras',
                  icon: <Icons.extras strokeWidth={1.75} />,
                },
              ]
            : []),
        ],
        isShown: (id) => isShown(id as MainSidebarNavItem),
        onToggle: (id) =>
          uiStore.toggleMainSidebarNavItem(id as MainSidebarNavItem),
      },
      { unique: true }
    );

  return (
    <Rail.Root labels={uiStore.navRailLabelsVisible}>
      <Rail.Scroll>
        <QuickCreateRail />
        <NewChatCoachMark>
          <Rail.Button
            id="new-chat-sidebar-item"
            label="New chat"
            hotkey="app.newChat"
            active={panel === 'chats'}
            onClick={(event) => openRoute(Routes.Chats, event)}
          >
            <Icons.newChat strokeWidth={1.75} />
          </Rail.Button>
        </NewChatCoachMark>
        {welcomeEnabled &&
          routeItem(
            Routes.Welcome,
            'Welcome',
            <Icons.hand strokeWidth={1.75} />
          )}
        {shownItems.map(railItem)}
        {pluginsStore.pages.map(({ id, label, icon: Icon }) => (
          <Rail.Button
            key={id}
            id={`plugin-${id}-sidebar-item`}
            label={label}
            active={pathname === `/p/${id}`}
            onClick={(event) => openRoute(`/p/${id}`, event)}
          >
            <Icon strokeWidth={1.75} />
          </Rail.Button>
        ))}
        <Rail.Button
          label="Search"
          hotkey="app.commandBar"
          onClick={() => query.toggle()}
        >
          <Icons.search strokeWidth={1.75} />
        </Rail.Button>
        {showExtras && <NavRailExtras />}
        <NavRailMore
          items={hiddenItems}
          active={hiddenItems.some(isItemActive)}
          showExtras={hasExtras && !showExtras}
          onSelect={selectItem}
          onHideWelcome={welcomeEnabled ? hideWelcome : undefined}
          onCustomize={openCustomize}
        />
      </Rail.Scroll>
      <ActiveMeetingPill variant="rail" />
      <NavRailUpdate />
      <CoachMarkTip
        mark={CONTEXT_COLLECTION_OFF_COACH_MARK}
        side="right"
        align="end"
      >
        <CoachMark.Anchor asChild>
          <span className="inline-flex">
            <ContextCollectionMenu />
          </span>
        </CoachMark.Anchor>
      </CoachMarkTip>
      <NavRailProfile />
    </Rail.Root>
  );
});

const NewChatCoachMark = observer(
  ({ children }: { children: React.ReactNode }) => (
    <CoachMark.Root
      open={webOnboardingThreadsStore.showNewChatCoachMark}
      onOpenChange={(open) => {
        if (!open) webOnboardingThreadsStore.dismissNewChatCoachMark();
      }}
    >
      <CoachMark.Anchor asChild>
        <span className="inline-flex">{children}</span>
      </CoachMark.Anchor>
      <CoachMark.Content side="right" align="start" sideOffset={12} width="sm">
        <CoachMark.Title>Start new chats here</CoachMark.Title>
        <CoachMark.Description>
          You can always ask littlebird about itself if you need help.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss asChild>
            <Button size="sm">Got it</Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark.Root>
  )
);
